import { sum, rollups } from "d3-array";

/**
 * Checks whether there's data for the tooltip or not
 * @param {*} key
 */
export const containsToolTipData = (key, seriesFilter, tooltipData) => {
  if (
    seriesFilter &&
    // checks if seriesFilter contains an element (array or object) which contains key or not
    seriesFilter.findIndex(
      (f) =>
        (Array.isArray(f) && f.indexOf(key) > -1) ||
        (typeof f === "string" && f.indexOf(key) > -1) ||
        f === key
    ) === -1
  )
    return false;

  let res = false;
  Object.keys(tooltipData).forEach((d) => {
    if (d.indexOf(key) > -1 || d.indexOf(key + ":") > -1 || d === key) {
      res = true;
    }
  });

  return res;
};

// Segments
export const formatDataForSegments = (data, dimensions, predictions = []) => {
  let _data = [];
  if (!data.error) {
    // fill data
    data.forEach((datum) => {
      datum.metrics.forEach((metric) => {
        const d = {
          date: datum.timestamp,
          metric: metric.friendlyName,
          metric_name: metric.name,
          metric_id: metric.id,
          value: metric.value,
        };

        // set prediction for metrics without breaking down by dimension
        if (predictions.length > 0 && !datum.dimensions) {
          const prediction = predictions.find(
            (p) =>
              p.metrics.findIndex(
                (pMetric) => pMetric.id.toString() === d.metric_id.toString()
              ) > -1
          );
          d.prediction = prediction ? prediction.metrics[0] : prediction;
        }

        datum.dimensions &&
          datum.dimensions.forEach((dim) => {
            d[dim.name] = dim.value;
            d[`${dim.name}_name`] = dim.valueFriendlyName;

            // set the prediction broken down by dimension
            if (predictions.length > 0) {
              // find the prediction
              const prediction = predictions.find(
                (p) =>
                  // according the right metric
                  p.metrics.findIndex(
                    (pm) => pm.id.toString() === metric.id.toString()
                  ) > -1 &&
                  // and according the right dimension id and value
                  p.dimensions.findIndex(
                    (pd) =>
                      pd.id.toString() === dim.id.toString() &&
                      pd.value === dim.value
                  ) > -1
              );
              d.prediction = prediction ? prediction.metrics[0] : prediction;
            }
          });

        // is there prediction?
        if (d.prediction) {
          // get the d.value + predicted to simulate a stacked value
          const total = d.value + d.prediction.predicted;
          // get the change in percentage in order to use it as reference to decrease the size of error bars
          const change = (d.prediction.predicted / total) * 100;

          /**
           * Returns the value increased by a percentage
           * @param {*} value
           * @returns
           */
          const calcPercentage = (value) => {
            return (value * change) / 100;
          };

          // set the deviation
          const [max, min] = d.prediction.predMaxMin;

          // gets the discrepancy between the max/min predicted and the current value
          const maxCalc = Math.abs(d.prediction.predicted - max);
          const minCalc = Math.abs(d.prediction.predicted - min);

          d.prediction = {
            ...d.prediction,
            // calculated prediction is a workaround to get the VALUE + PREDICTION and set it into a different property in order
            // to pretend to have a stacked bar. Currently, ErrorBar doesn't work with stacked bar, however, we  were able to
            // make something that looks close to stacked bar
            predictedCalculated: total,
            // error bar values decreased by the percentage of the change related to the sum of prediction + value. The change must be considerated in order
            // to render the right range of the bar/error
            deviationCalculated: [
              maxCalc - calcPercentage(maxCalc),
              minCalc - calcPercentage(minCalc),
            ],
            maxValue: max || d.value,
          };
        }

        _data.push(d);
      });
    });

    // aggregate data
    const aggrData = [];
    const reduce = (leaves, key) => {
      if (leaves.length) {
        const leaf = leaves[0];

        const d = {
          metric: leaf.metric,
          metric_name: leaf.metric_name,
          metric_id: leaf.metric_id,

          value: sum(leaves, (d) => d.value), // aggregate by the last dimension
        };

        // does the leaf has prediction?
        if (leaf.prediction) d.prediction = leaf.prediction;

        dimensions.forEach((dim) => {
          d[dim.name] = leaf[dim.name];
          d[`${dim.name}_name`] = leaf[`${dim.name}_name`];
          d.dimensionFriendlyName = leaf[`${dim.name}_name`];
        });

        aggrData.push(d);
        return d;
      }
    };

    const keys = [(d) => d.metric];

    // Use friendly names where possible
    dimensions.forEach((dim) =>
      keys.push((d) =>
        d[dim.name] !== "undefined" ? d[`${dim.name}_name`] : d[dim.name]
      )
    );

    const segmentsNestedData = rollups(_data, reduce, ...keys);

    return {
      segmentsData: aggrData.sort((a, b) => {
        // metric
        if (a.metric > b.metric) return 1;
        if (a.metric < b.metric) return -1;

        // dimensions
        let order = 0;
        dimensions.forEach((dim) => {
          if (a[dim.name] > b[dim.name]) {
            if (order === 0) order = 1;
          }
          if (a[dim.name] < b[dim.name]) {
            if (order === 0) order = -1;
          }
        });
        if (order !== 0) return order;

        // value
        if (a.value >= b.value) return -1;
        if (a.value < b.value) return 1;

        return 1;
      }),
      segmentsNestedData,
    };
  } else {
    return data.error;
  }
};

/**
 * Prepare the data for the chart, keys, export data and other maps
 * @param {*} dataGroupedByDate Object containing the hashmap of key/value pairs grouped by dates
 * @param {*} setJoinValue When `true`, set the props regarding metrics to the last element of the pure Data (no pred data)
 * in order to make it visible like a join between lines. For Bar charts it should be avoided. It's `false` by default.
 * @returns
 */
export const formatDataForChart = (dataGroupedByDate, setJoinValue = false) => {
  if (!dataGroupedByDate.error) {
    const _data = [];
    let all_dim_keys = [];
    let dimensionsMap = [];
    const exportData = [];
    const dataKeys = Object.keys(dataGroupedByDate);
    dataKeys.sort().forEach((date, index) => {
      const d = { date };
      dataGroupedByDate[date].forEach((datum) => {
        datum.metrics.forEach((metric) => {
          let export_d = { date };
          let name = metric.id;

          if (datum.dimensions && datum.dimensions.length) {
            const dim_key = datum.dimensions
              .map((d) => {
                export_d[d.name] = d.valueFriendlyName;
                return d.valueFriendlyName;
              })
              .join(":");

            const dimensionsFriendlyNames = datum.dimensions.map((d) => {
              return {
                name: d.value,
                friendlyName: d.valueFriendlyName,
              };
            });

            if (!all_dim_keys.includes(dim_key)) {
              all_dim_keys.push(dim_key);
              dimensionsMap.push(dimensionsFriendlyNames);
            }

            name += ":" + dim_key;
          }

          d[name] = metric.value;

          // does this metric has prediction?
          if (metric.predicted) {
            d[name + ".pred"] = metric.predicted;
            d[name + ".predMinMax"] = [
              metric.predMaxMin[1],
              metric.predMaxMin[0],
            ];
            d[name + ".accuracy"] = metric.accuracy;
            // the deviation price which is the distorcion between the predicted value + || - the Max/Min values
            const [max, min] = metric.predMaxMin;
            d[name + ".deviation"] = [
              Math.abs(metric.predicted - min),
              Math.abs(metric.predicted - max),
            ];
          }
          // should we make the join values && if it has not reached out the end of the array
          else if (setJoinValue && index < dataKeys.length) {
            // in the case the current metric has no prediction, BUT, the next one has, we need
            // to assign the point zero here in order to make the connection between the line charts
            // to avoid gaps between the different lines which will look like connected
            const currentElement = dataGroupedByDate[dataKeys[index + 1]];
            if (
              currentElement &&
              currentElement[0].metrics &&
              currentElement[0].metrics[0].predicted
            ) {
              // assign the last non predicted metric with props related to the metric, but,
              // with its current value to signal  point zero
              d[name + ".pred"] = metric.value;
              d[name + ".predMinMax"] = [metric.value, metric.value];
              d[name + ".accuracy"] = 0;
            }
          }

          export_d["metric"] = metric.friendlyName;
          export_d["value"] = metric.value;

          exportData.push(export_d);
        });
      });
      _data.push(d);

      all_dim_keys = all_dim_keys.sort();
    });
    return { chartData: _data, all_dim_keys, exportData, dimensionsMap };
  } else {
    return dataGroupedByDate.error;
  }
};

/**
 * Filter the metrics by removing the ones related to predictions
 * @param {*} metric
 * @returns
 */
export const excludePreditionMetrics = (metric) =>
  [".pred", ".deviation", ".accuracy"].filter(
    (preProp) => metric && metric.toString().indexOf(preProp) >= 0
  ).length === 0;

/**
 * Returns a new list of legends excluding the prediction related element
 */
export const excludePredictionsLegends = (legends = []) => {
  const excludedLegends = [".pred", ".deviation", ".predMinMax", ".accuracy"];
  const newLegendList = [];
  legends
    .filter(
      (legend) =>
        !excludedLegends.some((el) => legend.name.toString().indexOf(el) >= 0)
    )
    .forEach((legend) =>
      newLegendList.push({
        ...legend,
        value: legend.value.filter(
          (l) =>
            excludedLegends.filter(
              (pred) => l.name.toString().indexOf(pred) > -1
            ).length === 0
        ),
      })
    );
  return newLegendList;
};

/**
 * Hashmap for the prediction name and its descriptions
 */
export const predictionLabels = {
  ".pred": "Predicted",
  ".predMinMax": "Min/Max",
  ".accuracy": "Accuracy",
  ".deviation": "Deviation",
};
// Table
export const formatDataForTable = (data, dimensions) => {
  let _data = [];
  if (!data.error) {
    // fill data
    data.forEach((datum) => {
      datum.metrics.forEach((metric) => {
        const d = {
          date: datum.timestamp,
          metric: metric.friendlyName,
          metric_name: metric.name,
          metric_id: metric.id,
          value: metric.value,
        };

        datum.dimensions &&
          datum.dimensions.forEach((dim) => {
            d[dim.name] = dim.valueFriendlyName;
          });

        _data.push(d);
      });
    });

    // aggregate data
    const aggrData = [];

    const keys = [(d) => d.metric];

    // Use friendly names where possible
    dimensions.forEach((dim) =>
      keys.push((d) =>
        d[dim.name] !== "undefined" ? d[`${dim.name}_name`] : d[dim.name]
      )
    );

    return {
      defaultTableData: _data,
      tableData: aggrData.sort((a, b) => {
        // metric
        if (a.metric > b.metric) return 1;
        if (a.metric < b.metric) return -1;

        // dimensions
        let order = 0;
        dimensions.forEach((dim) => {
          if (a[dim.name] > b[dim.name]) {
            if (order === 0) order = 1;
          }
          if (a[dim.name] < b[dim.name]) {
            if (order === 0) order = -1;
          }
        });
        if (order !== 0) return order;

        // value
        if (a.value >= b.value) return -1;
        if (a.value < b.value) return 1;

        return 1;
      }),
    };
  } else {
    return data.error;
  }
};

export const formatDataForPivotTable = (data, state) => {
  if (data.error) return;

  let pivotTableData = [];

  for (let i = 0; i < data.length; i++) {
    let datum = {};

    datum.date = data[i].timestamp;

    // Metrics
    for (let m = 0; m < data[i].metrics.length; m++) {
      datum[data[i].metrics[m].friendlyName] = data[i].metrics[m].value;
    }

    // Dimensions
    if (data[i].dimensions) {
      for (let d = 0; d < data[i].dimensions.length; d++) {
        datum[data[i].dimensions[d].friendlyName] =
          typeof data[i].dimensions[d].value === "string"
            ? data[i].dimensions[d].valueFriendlyName
            : data[i].dimensions[d].value;
      }
    }

    pivotTableData.push(datum);
  }

  return pivotTableData;
};

/**
 * Return true/false if the current metric contains some word related to prediction
 * @param {*} metricName
 * @returns
 */
export const containsPredictionsWords = (metricName) => {
  return [".pred", ".deviation", ".predMinMax", ".accuracy"].some(
    (predictionName) => predictionName.indexOf(metricName.toString()) > -1
  );
};

/**
 * Returns true or false if the metric is a prediciton or not
 * @param {*} metric
 * @returns
 */
export const metricIsNotAPrediction = (metric) =>
  predictionWords.filter((pw) => metric.toString().includes(pw)).length === 0;

/**
 * Contains all words related to predictions
 */
export const predictionWords = [
  ".pred",
  ".deviation",
  ".predMinMax",
  ".accuracy",
];

/**
 * Given a payload, extract the data related to Prediction to be used in the QueryBuilderTooltipPrediction
 * @param {*} payload
 * @returns
 */
export const extractPredictionData = (payload) => {
  const dataKey = payload.dataKey.replace(".pred", "");
  const metricId = payload.dataKey.split(":")[0];
  const predictionKeys = {};

  predictionWords.forEach(
    (word) =>
      (predictionKeys[word.replace(".", "")] = payload.payload[dataKey + word])
  );
  return {
    metric_id: metricId,
    color: payload.fill,
    prediction: predictionKeys,
  };
};

/**
 * Given a dataKey, checks whether it contains some word related to predictions.
 * @param {*} dataKey
 * @returns
 */
export const isDataKeyAPrediction = (dataKey = "") =>
  dataKey.toString().includes(".pred");

export const formatMetricKey = (key) => {
  return key
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};
